import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { useAppAuth } from "@/auth/AppAuthProvider";
import MapAnnotator from "@/components/MapAnnotator";

const LayeredMapReview = React.lazy(() => import('../components/LayeredMapReview'));

export default function TerminalMapPopout() {
  const { id } = useParams();
  const { hasCapability } = useAppAuth();
  const [terminal, setTerminal] = useState(null);
  const [displayMap, setDisplayMap] = useState(null);
  const [berths, setBerths] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Terminal Map · Portcall HQ";
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [terminals, terminalBerths, maps] = await Promise.all([
          api.list("terminals"),
          api.list("berths", { terminal_id: id }),
          api.list("maps", { terminal_id: id }),
        ]);
        if (cancelled) return;
        const found = terminals.find(item => item.id === id);
        if (!found) {
          setError("Terminal not found");
          return;
        }
        setTerminal(found);
        setBerths(terminalBerths);
        setDisplayMap(maps.find(item => item.status === "published") || maps[maps.length - 1] || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || "Failed to load terminal");
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (error) return (
    <div className="min-h-screen bg-[#F3F6F7] text-slate-900 grid place-items-center p-6" data-testid="popout-error">
      <div className="panel p-6 max-w-md text-sm">
        <div className="font-heading text-lg font-black mb-2">Unable to open map</div>
        <div className="text-slate-400">{error}</div>
        <button type="button" onClick={() => window.close()} className="mt-4 px-3 py-1.5 border border-[#1E293B] text-xs font-mono text-slate-300 hover:text-sky-300">Close window</button>
      </div>
    </div>
  );

  if (!terminal) return (
    <div className="min-h-screen bg-[#F3F6F7] text-slate-500 grid place-items-center font-mono text-xs" data-testid="popout-loading">Loading terminal…</div>
  );

  return (
    <div className="min-h-screen bg-[#F3F6F7] text-slate-900 p-4" data-testid="popout-root">
      <header className="flex items-center justify-between border border-slate-200 bg-white rounded-xl px-4 py-3 mb-4 shadow-sm">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Terminal map · Portcall HQ</div>
          <div className="font-heading text-lg md:text-xl font-black tracking-tight" data-testid="popout-terminal-name">{terminal.name}</div>
          <div className="font-mono text-[10px] text-slate-500">{terminal.location_name} · {terminal.timezone}</div>
        </div>
        <button type="button" onClick={() => window.close()} className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-[10px] font-mono inline-flex items-center gap-1.5 text-slate-600 hover:text-sky-700" data-testid="popout-close" title="Close window">
          <X size={12} /> Close
        </button>
      </header>

      <main className="max-w-[1800px] mx-auto">{new URLSearchParams(window.location.search).get("layered")==="1" ? <React.Suspense fallback={<p>Loading layered map…</p>}><LayeredMapReview terminalId={id} pipelineId={new URLSearchParams(window.location.search).get("pipeline") || undefined}/></React.Suspense> : <MapAnnotator key={`${terminal.id}-${terminal.revision || 0}`} terminalId={terminal.id} mapFile={displayMap} berths={berths} terminalName={terminal.name} />}</main>
      {!hasCapability("map.manage") && <div className="mt-3 text-[10px] font-mono text-slate-500">Read-only view · map editing requires the reviewer role.</div>}
    </div>
  );
}
